import { BrowserRouter, Link, Navigate, NavLink, Route, Routes, useLocation } from 'react-router-dom';
import { Meldungsleiste } from './gemeinsam/komponenten/Meldungen';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { AuthProvider, useAuth } from './gemeinsam/lib/AuthContext';
import { istApp } from './bereiche/anmeldung/lib/desktop';
import { ANBIETER, ANBIETER_URL, PRODUKT } from './gemeinsam/lib/marke';
import { SprachProvider } from './gemeinsam/lib/i18n';
import { BenachrichtigungsProvider } from './bereiche/aufgaben/lib/benachrichtigungen';
import Layout from './gemeinsam/komponenten/Layout';
import { Spinner } from './gemeinsam/komponenten/Basis';
import Anmelden from './bereiche/anmeldung/seiten/Anmelden';
import Uebersicht from './bereiche/start/seiten/Uebersicht';
import Aufgaben from './bereiche/aufgaben/seiten/Aufgaben';
import Retouren from './bereiche/artikel/seiten/Retouren';
import Team from './bereiche/benutzer/seiten/Team';
import MitarbeiterProfil from './bereiche/benutzer/seiten/MitarbeiterProfil';
import Download from './bereiche/start/seiten/Download';
import AppHerunterladen from './bereiche/start/komponenten/AppHerunterladen';
import Tarif from './bereiche/inhaber/seiten/Tarif';
import Kunden from './bereiche/kontakte/seiten/Kunden';
import KundenAkte from './bereiche/kontakte/seiten/KundenAkte';
import Mandanten from './bereiche/mandanten/seiten/Mandanten';
import Einstellungen from './bereiche/firma/seiten/Einstellungen';
import Lieferanten from './bereiche/kontakte/seiten/Lieferanten';
import Lager from './bereiche/artikel/seiten/Lager';
import Kasse from './bereiche/belege/seiten/Kasse';
import Rechnungen from './bereiche/belege/seiten/Rechnungen';
import Belege from './bereiche/belege/seiten/Belege';
import Start from './bereiche/start/seiten/Start';
import Registrieren from './bereiche/anmeldung/seiten/Registrieren';
import MehrSeite from './app/MehrSeite';
import Buchhaltung from './bereiche/buchhaltung/seiten/Buchhaltung';
import Chat from './bereiche/aufgaben/seiten/Chat';
import Assistent from './bereiche/ki/seiten/Assistent';
import Support from './bereiche/support/seiten/Support';
import SupportPosteingang from './bereiche/support/komponenten/SupportPosteingang';
import Abrechnung from './bereiche/inhaber/seiten/Abrechnung';
import { api } from './gemeinsam/lib/api';
import Uebernahme from './bereiche/mandanten/seiten/Uebernahme';
import Firma from './bereiche/firma/seiten/Firma';
import GoBD from './bereiche/gobd/seiten/GoBD';
import Bank from './bereiche/bank/seiten/Bank';
import PasswortWechseln from './bereiche/anmeldung/seiten/PasswortWechseln';
import MailBestaetigen from './bereiche/anmeldung/seiten/MailBestaetigen';
import BestaetigungEinloesen from './bereiche/anmeldung/seiten/BestaetigungEinloesen';
import PasswortVergessen from './bereiche/anmeldung/seiten/PasswortVergessen';
import PasswortNeu from './bereiche/anmeldung/seiten/PasswortNeu';
import Anfragen from './bereiche/mandanten/seiten/Anfragen';
import Impressum from './bereiche/rechtliches/seiten/Impressum';
import Datenschutz from './bereiche/rechtliches/seiten/Datenschutz';
import Agb from './bereiche/rechtliches/seiten/Agb';
import { Kontakt, VertraegeKuendigen } from './bereiche/rechtliches/seiten/Kontakt';
import MandatErforderlich from './bereiche/anmeldung/seiten/MandatErforderlich';
import VertragBeendet from './bereiche/anmeldung/seiten/VertragBeendet';
import Biometriesperre from './bereiche/anmeldung/komponenten/Biometriesperre';
import FahrtenbuchSeite from './bereiche/buchhaltung/seiten/Fahrtenbuch';
import WasIstNeu from './bereiche/neuigkeiten/komponenten/WasIstNeu';
import NeuigkeitenVerwaltung from './bereiche/neuigkeiten/seiten/NeuigkeitenVerwaltung';

/**
 * Drei Hürden vor der Anwendung, in dieser Reihenfolge:
 *   1. Ein übergebenes Passwort muss ersetzt werden.
 *   2. Die E-Mail-Adresse muss bestätigt sein — ohne sie erreichen den Betrieb
 *      weder Anmeldecodes noch Rechnungen noch Lastschriftankündigungen.
 *   3. Das SEPA-Lastschriftmandat muss vorliegen.
 *
 * Die Reihenfolge ist nicht beliebig: wer seine Adresse noch nicht bestätigt
 * hat, soll nicht schon seine Bankverbindung eintippen.
 */
function Geschuetzt() {
  const { benutzer, laedt, passwortWechselErforderlich, mailBestaetigung, mandat, abo } = useAuth();
  const amAnfang = useLocation().pathname === '/';
  if (laedt) {
    return <div className="flex h-full items-center justify-center"><Spinner /></div>;
  }
  // Ohne Anmeldung landet man auf der Startseite, nicht auf einer Fehlermeldung.
  // In der App gibt es keine Startseite — Handy, Desktop-App und installierte
  // Web-App zeigen sofort die Anmeldung: wer sie installiert hat, muss nicht
  // mehr überzeugt werden, er will sich anmelden.
  if (!benutzer) {
    return amAnfang ? (istApp() ? <Anmelden /> : <Start />) : <Navigate to="/" replace />;
  }
  // Vertrag beendet: vor allen anderen Hürden — ein Mandat braucht es dann nicht mehr.
  if (abo?.phase === 'beendet' && !benutzer.isOwner) return <VertragBeendet />;
  if (passwortWechselErforderlich) return <PasswortWechseln />;
  if (mailBestaetigung?.noetig) return <MailBestaetigen />;
  if (mandat?.gesperrt) return <MandatErforderlich />;
  return <><Layout /><WasIstNeu /></>;
}

/**
 * Seiten nur für Administratoren. Das Menü blendet sie für Mitarbeiter aus;
 * wer die Adresse trotzdem aufruft, landet auf der Übersicht statt vor einer
 * Seite voller Fehlermeldungen. Geschützt sind die Daten auf dem Server.
 */
function NurAdmin({ children }: { children: ReactNode }) {
  const { istAdmin } = useAuth();
  return istAdmin ? <>{children}</> : <Navigate to="/" replace />;
}

// Zwei Reiter: Kunden verwalten und Kunden antworten. Die Adresse trägt den
// Reiter, damit der Link in der Support-Mail direkt im Posteingang landet.
function OwnerReiter({ ungelesen }: { ungelesen: number }) {
  return (
    <nav className="border-b border-slate-200 bg-white px-4 lg:px-8 dark:border-slate-800 dark:bg-slate-900">
      <div className="mx-auto flex max-w-6xl gap-1">
        {([['/owner', 'Mandanten', 0], ['/owner/abrechnung', 'Abrechnung', 0],
           ['/owner/support', 'Support', ungelesen], ['/owner/anfragen', 'Anfragen', 0],
           ['/owner/neuigkeiten', 'Neuigkeiten', 0]] as const).map(([ziel, name, n]) => (
          <NavLink key={ziel} to={ziel} end
                   className={({ isActive }) => `-mb-px flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition ${isActive
                     ? 'border-marke-600 text-marke-700 dark:border-marke-400 dark:text-marke-300'
                     : 'border-transparent text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'}`}>
            {name}
            {n > 0 && <span className="rounded-full bg-rose-600 px-1.5 text-[11px] font-bold text-white">{n}</span>}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

// Der Owner-Bereich liegt bewusst ausserhalb des Kunden-Layouts: eigene Adresse,
// eigene Kopfzeile, keine Kunden-Navigation. So ist beim Arbeiten immer klar,
// ob man im eigenen Betrieb oder in der Kundensteuerung ist.
function OwnerBereich() {
  const { benutzer, mandant, laedt, istOwner, abmelden, passwortWechselErforderlich } = useAuth();
  const pfad = useLocation().pathname;
  const imSupport = pfad.startsWith('/owner/support');
  const inAbrechnung = pfad.startsWith('/owner/abrechnung');
  const inNeuigkeiten = pfad.startsWith('/owner/neuigkeiten');
  const inAnfragen = pfad.startsWith('/owner/anfragen');
  const [ungelesen, setUngelesenRoh] = useState(0);
  const setUngelesen = useCallback((n: number) => setUngelesenRoh(n), []);

  // Der Zähler soll auch im Reiter „Mandanten" stimmen — sonst sieht man neue
  // Anfragen erst, wenn man schon im Posteingang ist.
  useEffect(() => {
    if (!istOwner) return;
    const holen = () => api<{ ungelesen: number }>('/support/owner/zaehler').then(d => setUngelesenRoh(d.ungelesen)).catch(() => {});
    void holen();
    const id = setInterval(holen, 60000);
    return () => clearInterval(id);
  }, [istOwner]);
  if (laedt) return <div className="flex h-full items-center justify-center"><Spinner /></div>;
  if (!benutzer) return <Navigate to="/anmelden" replace />;
  if (passwortWechselErforderlich) return <PasswortWechseln />;
  if (!istOwner) return <Navigate to="/" replace />;

  return (
    <div className="min-h-full bg-slate-100 dark:bg-slate-950">
      <header className="border-b border-slate-800 bg-slate-900 px-4 py-3 lg:px-8">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-semibold text-white">{PRODUKT} · Kundensteuerung</p>
            <p className="text-xs text-slate-400">
              {mandant?.name} · {benutzer?.name} ·{' '}
              <a href={ANBIETER_URL} target="_blank" rel="noopener noreferrer" className="hover:underline">
                {ANBIETER}
              </a>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" className="rounded-lg px-3 py-1.5 text-sm text-slate-300 hover:bg-slate-800">
              Zur eigenen Verwaltung
            </Link>
            <button onClick={abmelden} className="rounded-lg px-3 py-1.5 text-sm text-slate-300 hover:bg-slate-800">
              Abmelden
            </button>
          </div>
        </div>
      </header>
      <OwnerReiter ungelesen={ungelesen} />
      <div className="p-4 lg:p-8">
        {imSupport ? <div className="mx-auto max-w-6xl"><SupportPosteingang beiZaehler={setUngelesen} /></div>
          : inAbrechnung ? <Abrechnung />
            : inNeuigkeiten ? <NeuigkeitenVerwaltung />
              : inAnfragen ? <Anfragen />
                : <Mandanten />}
      </div>
    </div>
  );

}

/**
 * Die Sperre der App liegt ÜBER allem, auch über dem Inhaberbereich und den
 * öffentlichen Seiten. Sie erscheint nur in der nativen Hülle und nur, wenn
 * jemand angemeldet ist — eine Anmeldeseite braucht keine zweite Sperre.
 */
function MitSperre({ children }: { children: ReactNode }) {
  const { gesperrt, benutzer } = useAuth();
  return (
    <>
      {children}
      {gesperrt && benutzer && <Biometriesperre />}
    </>
  );
}

function Routen() {
  const { benutzer, laedt } = useAuth();
  return (
    <Routes>
      <Route
        path="/anmelden"
        element={laedt ? <div className="flex h-full items-center justify-center"><Spinner /></div>
          : benutzer ? <Navigate to="/" replace /> : <Anmelden />}
      />
      {/* Der Link aus der Bestätigungsmail — ohne Anmeldung erreichbar, weil
          er meist im Mailprogramm und oft auf einem anderen Gerät geöffnet wird. */}
      <Route path="/bestaetigen" element={<BestaetigungEinloesen />} />
      {/* Passwort vergessen: anfordern und der Link aus der Mail (30 Minuten gültig). */}
      <Route path="/passwort-vergessen" element={<PasswortVergessen />} />
      <Route path="/passwort-neu" element={<PasswortNeu />} />
      {/* Rechtliches — öffentlich, auch für angemeldete Nutzer und in den Apps. */}
      <Route path="/impressum" element={<Impressum />} />
      <Route path="/datenschutz" element={<Datenschutz />} />
      <Route path="/agb" element={<Agb />} />
      <Route path="/kontakt" element={<Kontakt />} />
      <Route path="/kuendigen" element={<VertraegeKuendigen />} />
      {/* Desktop-App installieren oder herunterladen — öffentlich, auch für die Startseite. */}
      <Route path="/download" element={<Download />} />
      <Route
        path="/registrieren"
        element={laedt ? <div className="flex h-full items-center justify-center"><Spinner /></div>
          : benutzer ? <Navigate to="/" replace /> : <Registrieren />}
      />
      {/* Die Wurzel zeigt Besuchern die Startseite und Angemeldeten ihre Übersicht.
          In der App gibt es keine Startseite: wer sie installiert hat, muss nicht
          mehr überzeugt werden — er will sich anmelden. Statt der Werbeseite
          kommt also sofort das Anmeldeformular. */}
      <Route element={<Geschuetzt />}>
        <Route path="/" element={<Uebersicht />} />
        <Route path="aufgaben" element={<Aufgaben />} />
        <Route path="retouren" element={<Retouren />} />
        <Route path="team" element={<Team />} />
        <Route path="team/:id" element={<MitarbeiterProfil />} />
        <Route path="tarif" element={<NurAdmin><Tarif /></NurAdmin>} />
        <Route path="kasse" element={<Kasse />} />
        <Route path="lager" element={<Lager />} />
        <Route path="belege" element={<Belege />} />
        <Route path="rechnungen" element={<Rechnungen />} />
        <Route path="kunden" element={<Kunden />} />
        <Route path="kunden/:id" element={<KundenAkte />} />
        <Route path="lieferanten" element={<Lieferanten />} />
        <Route path="buchhaltung" element={<NurAdmin><Buchhaltung /></NurAdmin>} />
        <Route path="chat" element={<Chat />} />
        <Route path="assistent" element={<Assistent />} />
        <Route path="support" element={<Support />} />
        {/* Nur in der App-Ansicht erreichbar; am Schreibtisch steht derselbe
            Inhalt am Fuß der Seitenleiste. */}
        <Route path="mehr" element={<MehrSeite />} />
        <Route path="firma" element={<NurAdmin><Firma /></NurAdmin>} />
        <Route path="gobd" element={<NurAdmin><GoBD /></NurAdmin>} />
        {/* Ausgaben sind seit 2026-10-12 ein Reiter der Buchhaltung. */}
        <Route path="ausgaben" element={<Navigate to="/buchhaltung?reiter=ausgaben" replace />} />
        <Route path="bank" element={<NurAdmin><Bank /></NurAdmin>} />
        <Route path="bank/rueckkehr" element={<NurAdmin><Bank /></NurAdmin>} />
        <Route path="fahrtenbuch" element={<NurAdmin><FahrtenbuchSeite /></NurAdmin>} />
        <Route path="uebernahme" element={<NurAdmin><Uebernahme /></NurAdmin>} />
        {/* Für alle: Konto, Zwei-Faktor, Geräte. Den Betriebsteil zeigt die Seite nur Administratoren. */}
        <Route path="einstellungen" element={<Einstellungen />} />
        <Route path="app" element={<div className="mx-auto max-w-4xl"><AppHerunterladen /></div>} />
      </Route>
      <Route path="/owner" element={<OwnerBereich />} />
      <Route path="/owner/support" element={<OwnerBereich />} />
      <Route path="/owner/abrechnung" element={<OwnerBereich />} />
      <Route path="/owner/neuigkeiten" element={<OwnerBereich />} />
      <Route path="/owner/anfragen" element={<OwnerBereich />} />
      {/* Alte Adresse weiterleiten */}
      <Route path="/mandanten" element={<Navigate to="/owner" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    // Läuft unter der Hauptdomain — kein basename nötig.
    <BrowserRouter>
      <SprachProvider>
        <AuthProvider>
          <BenachrichtigungsProvider>
            <MitSperre>
              <Routen />
            </MitSperre>
            <Meldungsleiste />
          </BenachrichtigungsProvider>
        </AuthProvider>
      </SprachProvider>
    </BrowserRouter>
  );
}
